import { classnames } from 'Utils/classnames/classnames.utils';
import React, { Fragment } from 'react';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Img } from 'Components/frameworks/image/Img';
import type { InspirationalPictureComponentType } from './InspirationalGrid';
export const InspirationalPicture = ({
  className,
  descriptionStyle,
  disableRelativePadding = false,
  imgClassName,
  index = 0,
  isWide = false,
  link,
  radius,
  ratioDisplay,
  ratioKey,
  subtitle,
  title,
  url,
}: InspirationalPictureComponentType) => {
  const { isRTL } = useResponsive();
  const track = useTracking();
  const padding = isWide ? 'calc(20 / 1440 * 100vw)' : 'min(1.25rem, calc(20 / 1180 * 100vw))';
  const descriptionText = (
    <div
      className={classnames('flex w-full items-center p-20 text-white', {
        'direction-rtl': isRTL,
      })}
      data-testid={`IG-item-${index}-link`}
      style={
        !disableRelativePadding
          ? {
              padding,
            }
          : {}
      }
    >
      <span>
        {title}
        {subtitle && (
          <Fragment>
            <br />
            <span className="font-sans text-12 sm:text-10 lg:text-12">{subtitle}</span>
          </Fragment>
        )}
      </span>
      {link && (
        <span className="transition-transform ms-auto transform ps-5 duration-250 ease-in group-hover:ltr:translate-x-1/2 rtl:-translate-x-1/2">
          <Icon name={ICONS.ARROW_RIGHT} hasRtl width="0.625rem" />
        </span>
      )}
    </div>
  );
  const content = (
    <Fragment>
      <Img
        altMsg={title}
        className={classnames(imgClassName, {
          'transition-transform transform duration-250 ease-in group-hover:scale-105 group-focus:scale-105':
            link,
        })}
        ratioDisplay={ratioDisplay}
        ratioKey={ratioKey}
        url={url}
      />

      {title && (
        <div
          className="absolute inset-0 flex items-end bg-[linear-gradient(0deg,rgba(0,0,0,0.8)_0%,transparent_100px)] font-brand text-16 leading-tight sm:text-14 lg:text-16"
          style={{ ...descriptionStyle }}
        >
          {descriptionText}
        </div>
      )}
    </Fragment>
  );
  return (
    <div
      className={classnames(
        'group relative isolate overflow-hidden',
        {
          'rounded-12': !radius,
        },
        className,
      )}
      data-testid={`IG-item-${index}`}
      style={
        radius && {
          borderRadius: radius + 'px',
        }
      }
    >
      {link ? (
        <DynamicLink
          className="w-full"
          onClick={() =>
            track({
              actionGA: 'inspirationalGrid',
              categoryGA: 'landing',
              labelGA: title,
              thumbnailPosition: index + 1,
            })
          }
          target="_blank"
          url={link}
        >
          {content}
        </DynamicLink>
      ) : (
        content
      )}
    </div>
  );
};
